import { isNil } from 'lodash-es';

import {
  type ConvertibleUnit,
  type FormatOptions,
  getFormatting,
  type Unit,
} from '@dynatrace-sdk/units';

import { normalize } from './normalize.js';
import type { Formatter } from '../../../types/formatter.js';
import { isFormatterOptions } from '../../../types/formatter.js';
import { SingleValueUnit } from '../types/single-value-base-props.js';

export const defaultFormatter = (
  value: number,
  unit?: string | Unit,
): [string, string?] => {
  const options: FormatOptions<Unit, ConvertibleUnit> = {
    maximumFractionDigits: 1,
  };

  if (unit && typeof unit === 'string') {
    return [getFormatting(value, options)[0].value, unit];
  }

  const parts = getFormatting(
    value,
    unit ? { ...options, input: unit as ConvertibleUnit } : options,
  );
  const formattedUnit =
    parts[0].symbol.length > 0 ? parts[0].symbol : undefined;
  return [parts[0].value, formattedUnit];
};

/**
 * Applies formatter or normalizes the value, depending on value type.
 *
 * @param value - number/string value
 * @param unit
 * @param formatter - optional formatter
 */
export function formatValue(
  value: string | number,
  unit?: SingleValueUnit,
  formatter?: Formatter | FormatOptions<Unit, ConvertibleUnit>,
): { value: string; unit?: string } {
  if (isNil(value)) {
    return {
      value: '',
    };
  }

  // format number values
  if (typeof value === 'number') {
    if (typeof unit === 'string') {
      const [formattedValue, formattedUnit] = defaultFormatter(value, unit);

      return {
        value: formattedValue,
        unit: formattedUnit,
      };
    }
    if (isFormatterOptions(formatter)) {
      const formattingParts = getFormatting(value, formatter);

      return {
        value: formattingParts[0].value,
        unit: formattingParts[0].symbol,
      };
    }
    if (formatter) {
      return extractValueAndUnit(formatter(value));
    }
    const [formattedValue, formattedUnit] = defaultFormatter(value, unit);

    return {
      value: formattedValue,
      unit: formattedUnit,
    };
  }

  // normalize string values
  return {
    value: normalize(`${value}`),
  };
}

/**
 * Extracts value and unit from a formatted string. It takes into account the variant 'less-than', which have an extra space between the '\<' symbol and the number
 * @param formattedValue - the formatted string
 */
export function extractValueAndUnit(formattedValue: string) {
  const parts = formattedValue.split(' ');

  let value: string;
  let unit: string;

  if (parts[0] === '<') {
    value = `${parts[0]} ${parts[1]}`;
    unit = parts[2];
  } else {
    value = parts[0];
    unit = parts[1];
  }

  return {
    value,
    unit,
  };
}
